import React, { useState, useEffect, Suspense } from 'react';
import SysOwnerSideBar from './SysOwnerSidebar';
import { useNavigate } from 'react-router-dom';
import * as mui from '@mui/material';
import ResponsiveContainer from '../../layout/Container';
import { createTheme, ThemeProvider, styled, alpha } from '@mui/material/styles';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import KeyIcon from '@mui/icons-material/Key';
import SettingsIcon from '@mui/icons-material/Settings';
import StorageIcon from '@mui/icons-material/Storage';
import BusinessIcon from '@mui/icons-material/Business';
import DescriptionIcon from '@mui/icons-material/Description';
import FilterListIcon from '@mui/icons-material/FilterList';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import appService from '../../../services/ApplicationService';
import userService from '../../../services/UserService';
import companyService from '../../../services/CompanyService';
import LogoLoader from '../../common/LogoLoader';

// Animation keyframes
const globalStyles = `
    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
    }
    @keyframes shimmer {
        0% { background-position: -1000px 0; }
        100% { background-position: 1000px 0; }
    }
    @keyframes subtle-pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.01); }
        100% { transform: scale(1); }
    }
    @keyframes gradient-shift {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
    }
    .hover-lift {
        transition: transform 0.3s cubic-bezier(0.2, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.2, 0, 0.2, 1);
    }
    .hover-lift:hover {
        transform: translateY(-5px);
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    }
    .premium-card {
        position: relative;
        isolation: isolate;
        overflow: hidden;
    }
    .premium-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(90deg, #3f87f5, #0b4a99, #42a5f5);
        background-size: 200% 200%;
        animation: gradient-shift 4s ease infinite;
        z-index: 1;
        opacity: 0;
        transition: opacity 0.3s ease;
    }
    .premium-card:hover::before {
        opacity: 1;
    }
    .glass-morphism {
        background: rgba(255, 255, 255, 0.65);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.18);
    }
    .section-header-glow {
        position: relative;
    }
    .section-header-glow::after {
        content: '';
        position: absolute;
        bottom: -2px;
        left: 0;
        right: 0;
        height: 1px;
        background: linear-gradient(90deg, rgba(25, 118, 210, 0.2), rgba(25, 118, 210, 0.7), rgba(25, 118, 210, 0.2));
    }
    .card-content-container {
        transition: all 0.3s ease;
    }
    .shimmer-bg {
        background: linear-gradient(90deg, rgba(249, 249, 249, 0.1), rgba(249, 249, 249, 0.3), rgba(249, 249, 249, 0.1));
        background-size: 1000px 100%;
        animation: shimmer 2s infinite linear;
    }
`;

// Styled components with more subtle styling
const NarrativeCard = styled(mui.Paper)(({ theme }) => ({
    borderRadius: theme.spacing(2.5),
    padding: theme.spacing(3.5),
    transition: 'all 0.3s cubic-bezier(0.2, 0, 0.2, 1)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: 'rgba(0, 0, 0, 0.02) 0px 2px 15px, rgba(0, 0, 0, 0.02) 0px 0px 0px 1px',
    backgroundImage: 'linear-gradient(to bottom, #ffffff, #fafbfd)',
    animation: 'fadeIn 0.5s ease-out',
    '&::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        zIndex: -1,
        opacity: 0,
        transition: 'opacity 0.4s ease',
        background: 'linear-gradient(145deg, rgba(255,255,255,0.6) 0%, rgba(240,249,255,0.4) 100%)',
    },
    '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: 'rgba(17, 12, 46, 0.08) 0px 12px 30px 0px, rgba(0, 0, 0, 0.01) 0px 2px 5px',
        backgroundImage: 'linear-gradient(to bottom, #ffffff, #f4f8ff)',
        '& .card-view-button': {
            color: theme.palette.primary.main,
            transform: 'translateX(4px)'
        },
        '&::after': {
            opacity: 1,
        }
    }
}));

const CardActionButton = styled(mui.Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    fontWeight: 600,
    transition: 'all 0.25s ease',
    borderTop: '1px solid',
    borderColor: 'rgba(0, 0, 0, 0.04)',
    paddingTop: theme.spacing(2),
    marginTop: theme.spacing(2),
    '&:hover': {
        color: theme.palette.primary.main,
        '& .action-icon': {
            transform: 'translateX(3px)',
            color: theme.palette.primary.main,
        }
    }
}));

const LoadingContainer = styled(mui.Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '300px',
    flexDirection: 'column',
}));

const CompanySection = styled(mui.Box)(({ theme }) => ({
    marginBottom: theme.spacing(4.5),
    animation: 'fadeIn 0.6s ease-out',
}));

const CompanyHeader = styled(mui.Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2.5),
    paddingBottom: theme.spacing(1.5),
    borderBottom: '1px solid',
    borderColor: alpha(theme.palette.divider, 0.1),
    position: 'relative',
    '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '10%',
        height: '2px',
        backgroundColor: theme.palette.primary.main,
        opacity: 0.6,
    }
}));

const FilterButton = styled(mui.Button)(({ theme }) => ({
    borderRadius: theme.spacing(1.5),
    textTransform: 'none',
    borderColor: 'rgba(0, 0, 0, 0.08)',
    color: theme.palette.text.primary,
    backgroundColor: 'white',
    fontSize: '0.875rem',
    fontWeight: 600,
    padding: '8px 18px',
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.03)',
    '&:hover': {
        backgroundColor: 'rgba(25, 118, 210, 0.04)',
        borderColor: theme.palette.primary.main,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
    }
}));

const ProcessNarrative = () => {
    const [apps, setApps] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [selectedApp, setSelectedApp] = useState(null);
    const [groupByCompany, setGroupByCompany] = useState(false);
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const navigate = useNavigate();

    // Add global styles
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = globalStyles;
        document.head.appendChild(style);
        
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                
                // Add a 2-second delay before fetching data
                await new Promise(resolve => setTimeout(resolve, 1000));
                
                // Fetch apps and users in parallel
                const [currentUserResponse, companyResponse] = await Promise.all([
                    userService.fetchCurrentUser().catch(() => null),
                    companyService.fetchCompanies().catch(() => null),
                ]);

                if (currentUserResponse) {
                    setCurrentUser(currentUserResponse);
                    const assignedApps = await appService.fetchAppsByOwner(currentUserResponse.id);
                    setApps(assignedApps);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setIsLoading(false);
            }
        };
        
        fetchData();
    }, []);

    const handleMenuClick = (event, app) => {
        setAnchorEl(event.currentTarget);
        setIsMenuOpen(true);
        setSelectedApp(app);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setIsMenuOpen(false);
        setSelectedApp(null);
    };
    
    const handleFilterClick = (event) => {
        setFilterAnchorEl(event.currentTarget);
    };
    
    const handleFilterClose = () => {
        setFilterAnchorEl(null);
    };
    
    const handleGroupByCompany = () => {
        setGroupByCompany(true);
        handleFilterClose();
    };
    
    const handleShowAll = () => {
        setGroupByCompany(false);
        handleFilterClose();
    };
    
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const StyledMenu = styled((props) => (
        <Menu
            elevation={0}
            anchorEl={anchorEl}
            open={isMenuOpen}
            onClose={handleMenuClose}
            {...props}
        />
    ))(({ theme }) => ({
        '& .MuiPaper-root': {
            borderRadius: 14,
            marginTop: theme.spacing(1.5),
            minWidth: 240,
            boxShadow: '0 8px 30px rgba(0,0,0,0.08), 0 0 1px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: alpha(theme.palette.divider, 0.1),
            backgroundImage: 'linear-gradient(to bottom, #ffffff, #fafbfd)',
            backdropFilter: 'blur(20px)',
            '& .MuiMenu-list': {
                padding: '8px',
            },
            '& .MuiMenuItem-root': {
                borderRadius: 10,
                margin: '4px 0',
                padding: '10px 16px',
                fontSize: '0.875rem',
                fontWeight: 500,
                transition: 'all 0.2s ease',
                '& .MuiSvgIcon-root': {
                    fontSize: 20,
                    marginRight: theme.spacing(2),
                    color: theme.palette.primary.main,
                    opacity: 0.8,
                },
                '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    '& .MuiSvgIcon-root': {
                        opacity: 1,
                        transform: 'scale(1.1)',
                    }
                },
                '&:active': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                }
            },
        },
    }));
    
    const FilterMenu = styled((props) => (
        <Menu
            elevation={0}
            anchorEl={filterAnchorEl}
            open={Boolean(filterAnchorEl)}
            onClose={handleFilterClose}
            {...props}
        />
    ))(({ theme }) => ({
        '& .MuiPaper-root': {
            borderRadius: 14,
            marginTop: theme.spacing(1.5),
            minWidth: 200,
            boxShadow: '0 8px 30px rgba(0,0,0,0.08), 0 0 1px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: alpha(theme.palette.divider, 0.1),
            backgroundImage: 'linear-gradient(to bottom, #ffffff, #fafbfd)',
            backdropFilter: 'blur(20px)',
            overflow: 'hidden',
            '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: 'linear-gradient(90deg, rgba(11, 87, 208, 0.2), rgba(11, 87, 208, 0.6), rgba(11, 87, 208, 0.2))',
                opacity: 0.8,
            },
            '& .MuiMenu-list': {
                padding: '8px',
            },
            '& .MuiMenuItem-root': {
                borderRadius: 10,
                margin: '4px 0',
                padding: '10px 16px',
                fontSize: '0.875rem',
                fontWeight: 500,
                transition: 'all 0.2s ease',
                position: 'relative',
                '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
                '&.Mui-selected': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '3px',
                        height: '50%',
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: '0 2px 2px 0',
                    },
                    '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.15),
                    }
                }
            },
        },
    }));

    const theme = createTheme({
        palette: {
            primary: {
                main: '#0b57d0',
                light: '#3d85e3',
                dark: '#0842a0'
            },
            secondary: {
                main: '#10b981',
                light: '#34d399',
                dark: '#059669'
            },
            text: {
                primary: '#0f172a',
                secondary: '#64748b',
            },
            divider: 'rgba(0, 0, 0, 0.06)',
            background: {
                default: '#f9fafb',
                paper: '#ffffff'
            }
        },
        typography: {
            fontFamily: "'Plus Jakarta Sans', 'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
            h4: {
                fontWeight: 700,
                letterSpacing: '-0.02em'
            },
            h6: {
                fontWeight: 600,
                letterSpacing: '-0.01em'
            },
            body1: {
                lineHeight: 1.7,
                fontSize: '0.95rem'
            },
            body2: {
                lineHeight: 1.6,
                fontSize: '0.875rem'
            },
            subtitle1: {
                fontWeight: 600,
                fontSize: '1rem'
            }
        },
        shape: {
            borderRadius: 14,
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        boxShadow: 'none',
                        fontWeight: 600,
                        borderRadius: 10,
                        '&:hover': {
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.07)',
                        }
                    },
                    contained: {
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
                    }
                }
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        boxShadow: 'none',
                    }
                }
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        fontWeight: 600
                    }
                }
            },
            MuiLink: {
                styleOverrides: {
                    root: {
                        textDecoration: 'none',
                        fontWeight: 500,
                        '&:hover': {
                            textDecoration: 'underline'
                        }
                    }
                }
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 12,
                        }
                    }
                }
            },
            MuiBreadcrumbs: {
                styleOverrides: {
                    root: {
                        '& .MuiBreadcrumbs-separator': {
                            color: 'rgba(100, 116, 139, 0.5)'
                        }
                    }
                }
            }
        }
    });
    
    // Filter apps based on search query
    const filteredApps = apps.filter(app => {
        if (!searchQuery) return true;
        
        const query = searchQuery.toLowerCase();
        return (
            (app.APP_NAME && app.APP_NAME.toLowerCase().includes(query)) ||
            (app.COMPANY_NAME && app.COMPANY_NAME.toLowerCase().includes(query)) ||
            (app.APP_DESCRIPTION && app.APP_DESCRIPTION.toLowerCase().includes(query))
        );
    });

    // Group apps by company if needed
    const groupedApps = filteredApps.reduce((groups, app) => {
        const company = app.COMPANY_NAME || 'Other';
        if (!groups[company]) {
            groups[company] = [];
        }
        groups[company].push(app);
        return groups;
    }, {});

    const renderAppCard = (app) => {
        return (
            <NarrativeCard className="premium-card hover-lift">
                <mui.Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                    <mui.Chip
                        label={app.COMPANY_NAME || 'No Company'}
                        size="small"
                        sx={{
                            height: '24px',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            backgroundColor: 'rgba(11, 87, 208, 0.08)',
                            color: 'primary.main',
                            borderRadius: '12px',
                            backdropFilter: 'blur(8px)',
                            letterSpacing: '0.02em',
                            '& .MuiChip-label': {
                                px: 1.5
                            }
                        }}
                    />
                </mui.Box>
                
                <mui.Typography 
                    variant="h6" 
                    sx={{ 
                        fontWeight: 700,
                        color: 'text.primary',
                        mb: 1.5,
                        fontSize: '1.05rem',
                        lineHeight: 1.3,
                        letterSpacing: '-0.01em',
                    }}
                >
                    {app.APP_NAME || 'Unnamed Application'}
                </mui.Typography>
                
                <mui.Typography 
                    variant="body2" 
                    sx={{ 
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        mb: 2.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.6,
                        flexGrow: 1
                    }}
                >
                    {app.APP_DESCRIPTION || 'No description available'}
                </mui.Typography>
                
                <CardActionButton 
                    onClick={() => navigate(`/processnarrative/${app.id}`)}
                    className="card-view-button"
                    sx={{ mt: 'auto', cursor: 'pointer' }}
                >
                    <span>View Narratives</span>
                    <mui.Box 
                        component="span" 
                        className="action-icon"
                        sx={{ 
                            display: 'flex',
                            alignItems: 'center',
                            transition: 'transform 0.3s ease-out',
                            color: 'text.secondary',
                        }}
                    >
                        <OpenInNewIcon sx={{ fontSize: 16, ml: 0.75 }} />
                    </mui.Box>
                </CardActionButton>
            </NarrativeCard>
        );
    };

    const customMainContent = (
        <div>
            <ResponsiveContainer>
                <mui.Box sx={{ padding: { xs: '16px', sm: '24px', md: '32px' } }}>
                    <mui.Breadcrumbs aria-label="breadcrumb" sx={{ opacity: 0.9 }}>
                        <mui.Link underline="hover" color="inherit" href="/dashboard" sx={{ display: 'flex', alignItems: 'center' }}>
                            <mui.SvgIcon fontSize="small" sx={{ mr: 0.5 }}>
                                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
                            </mui.SvgIcon>
                            Dashboard
                        </mui.Link>
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            My Dashboard
                        </mui.Link>
                        <mui.Typography color="text.primary" sx={{ fontWeight: 500 }}>Process Narrative</mui.Typography>
                    </mui.Breadcrumbs>

                    <mui.Box 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            mb: 5, 
                            mt: 3,
                            position: 'relative',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: -16,
                                left: 0,
                                width: '80px',
                                height: '4px',
                                background: 'linear-gradient(90deg, #0b57d0, rgba(11, 87, 208, 0.4))',
                                borderRadius: '4px',
                            }
                        }}
                    >
                        <mui.Box 
                            sx={{ 
                                backgroundColor: 'rgba(11, 87, 208, 0.08)', 
                                p: 1.5, 
                                borderRadius: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mr: 3,
                                boxShadow: '0 4px 10px rgba(11, 87, 208, 0.08)'
                            }}
                        >
                            <AutoStoriesIcon sx={{ fontSize: 36, color: 'primary.main' }} />
                        </mui.Box>
                        <mui.Box>
                            <mui.Typography variant="h4" component="h1" sx={{ 
                                fontWeight: 800, 
                                color: 'text.primary', 
                                mb: 1,
                                letterSpacing: '-0.02em',
                                fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' }
                            }}>
                                Process Narratives
                            </mui.Typography>
                            <mui.Typography variant="body1" sx={{ 
                                color: 'text.secondary', 
                                maxWidth: '700px',
                                fontSize: '0.95rem'
                            }}>
                                View and download process narratives for audit reporting and compliance documentation.
                            </mui.Typography>
                        </mui.Box>
                    </mui.Box>

                    {isLoading ? (
                        <mui.Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center', 
                            minHeight: '300px',
                            flexDirection: 'column'
                        }}>
                            <LogoLoader 
                                size={85} 
                                message="Loading Narratives..." 
                                duration={1.5} 
                            />
                            <mui.Typography
                                variant="body2"
                                sx={{ 
                                    mt: 3, 
                                    color: 'text.secondary',
                                    fontSize: '0.875rem',
                                    opacity: 0.8
                                }}
                            >
                                Retrieving your application data...
                            </mui.Typography>
                        </mui.Box>
                    ) : (
                        <>
                            <mui.Box sx={{ 
                                backgroundColor: 'rgba(250, 252, 254, 0.97)', 
                                borderRadius: '20px',
                                minHeight: '80vh',
                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.02), 0 0 0 1px rgba(0, 0, 0, 0.03)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid',
                                borderColor: 'rgba(255, 255, 255, 0.7)'
                            }}>
                                <mui.Paper 
                                    elevation={0}
                                    sx={{ 
                                        p: { xs: 2, sm: 3, md: 4 },
                                        height: '100%',
                                        backgroundColor: 'transparent',
                                        borderRadius: '20px',
                                    }}
                                >
                                    <mui.Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center', 
                                        mb: 5,
                                        pb: 2.5,
                                        borderBottom: '1px solid',
                                        borderColor: 'rgba(0, 0, 0, 0.04)',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        gap: { xs: 2, sm: 0 }
                                    }}>
                                        <mui.Typography 
                                            variant="h6" 
                                            sx={{ 
                                                fontWeight: 700, 
                                                color: 'text.primary',
                                                position: 'relative',
                                                '&::after': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    bottom: -10,
                                                    left: 0,
                                                    width: '40px',
                                                    height: '3px',
                                                    backgroundColor: 'primary.main',
                                                    borderRadius: '2px',
                                                    opacity: 0.7
                                                }
                                            }}
                                        >
                                            All Applications
                                        </mui.Typography>

                                        <mui.Box sx={{ 
                                            display: 'flex', 
                                            gap: 2,
                                            width: { xs: '100%', sm: 'auto' },
                                            justifyContent: { xs: 'space-between', sm: 'flex-end' }
                                        }}>
                                            <mui.TextField
                                                placeholder="Search applications..."
                                                size="small"
                                                value={searchQuery}
                                                onChange={handleSearchChange}
                                                InputProps={{
                                                    startAdornment: (
                                                        <mui.InputAdornment position="start">
                                                            <mui.SvgIcon fontSize="small" color="action" sx={{ opacity: 0.8 }}>
                                                                <path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 0 0 1.48-5.34c-.47-2.78-2.79-5-5.59-5.34a6.505 6.505 0 0 0-7.27 7.27c.34 2.8 2.56 5.12 5.34 5.59a6.5 6.5 0 0 0 5.34-1.48l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0 .41-.41.41-1.08 0-1.49L15.5 14zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                                                            </mui.SvgIcon>
                                                        </mui.InputAdornment>
                                                    ),
                                                }}
                                                sx={{
                                                    width: { xs: '65%', sm: '260px' },
                                                    backgroundColor: 'white',
                                                    borderRadius: 3,
                                                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.02)',
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 3,
                                                        borderColor: 'rgba(0, 0, 0, 0.06)',
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: 'rgba(11, 87, 208, 0.3)',
                                                        },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: 'rgba(11, 87, 208, 0.6)',
                                                            borderWidth: '1px',
                                                        }
                                                    }
                                                }}
                                            />
                                            
                                            <FilterButton
                                                variant="outlined"
                                                startIcon={<FilterListIcon />}
                                                onClick={handleFilterClick}
                                                sx={{ 
                                                    minWidth: { xs: '30%', sm: 'auto' }
                                                }}
                                            >
                                                {groupByCompany ? 'Grouped' : 'View Options'}
                                            </FilterButton>
                                        </mui.Box>
                                    </mui.Box>
                                    
                                    {groupByCompany ? (
                                        // Display grouped by company
                                        Object.entries(groupedApps).length > 0 ? (
                                            Object.entries(groupedApps).map(([company, companyApps]) => (
                                                <CompanySection key={company}>
                                                    <CompanyHeader>
                                                        <mui.Box 
                                                            sx={{ 
                                                                backgroundColor: 'rgba(11, 87, 208, 0.06)', 
                                                                p: 1, 
                                                                borderRadius: '10px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                mr: 1.5
                                                            }}
                                                        >
                                                            <BusinessIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                                        </mui.Box>
                                                        <mui.Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.925rem' }}>
                                                            {company}
                                                        </mui.Typography>
                                                        <mui.Chip 
                                                            label={`${companyApps.length} ${companyApps.length === 1 ? 'application' : 'applications'}`} 
                                                            size="small"
                                                            sx={{ 
                                                                ml: 2,
                                                                height: 24, 
                                                                fontSize: '0.7rem',
                                                                fontWeight: 600,
                                                                backgroundColor: 'rgba(11, 87, 208, 0.06)',
                                                                color: 'primary.dark',
                                                                borderRadius: '12px',
                                                                '& .MuiChip-label': {
                                                                    px: 1.5
                                                                }
                                                            }}
                                                        />
                                                    </CompanyHeader>
                                                    <mui.Grid container spacing={3}>
                                                        {companyApps.map((app, index) => (
                                                            <mui.Grid item xs={12} sm={6} md={4} key={index}>
                                                                {renderAppCard(app)}
                                                            </mui.Grid>
                                                        ))}
                                                    </mui.Grid>
                                                </CompanySection>
                                            ))
                                        ) : (
                                            <EmptyStateMessage query={searchQuery} />
                                        )
                                    ) : (
                                        // Display all applications in a grid
                                        filteredApps.length > 0 ? (
                                            <mui.Grid container spacing={3}>
                                                {filteredApps.map((app, index) => (
                                                    <mui.Grid item xs={12} sm={6} md={4} key={index}>
                                                        {renderAppCard(app)}
                                                    </mui.Grid>
                                                ))}
                                            </mui.Grid>
                                        ) : (
                                            <EmptyStateMessage query={searchQuery} />
                                        )
                                    )}
                                </mui.Paper>
                            </mui.Box>
                        </>
                    )}
                </mui.Box>
            </ResponsiveContainer>
        </div>
    );
    
    // Empty state component
    const EmptyStateMessage = ({ query }) => (
        <mui.Box 
            sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: 'white',
                borderRadius: '16px',
                py: 8,
                px: 4,
                mt: 4,
                boxShadow: '0 8px 20px rgba(0,0,0,0.03)',
                border: '1px solid',
                borderColor: 'rgba(0, 0, 0, 0.04)'
            }}
        >
            <mui.Box 
                sx={{ 
                    p: 2.5, 
                    borderRadius: '50%', 
                    backgroundColor: 'rgba(11, 87, 208, 0.04)', 
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <mui.SvgIcon sx={{ fontSize: 42, color: 'primary.main', opacity: 0.7 }}>
                    <path d="M11 15h2v2h-2v-2zm0-8h2v6h-2V7zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
                </mui.SvgIcon>
            </mui.Box>
            <mui.Typography variant="h6" sx={{ color: 'text.primary', mb: 1.5, fontWeight: 700 }}>
                {query ? 'No applications found' : 'No applications available'}
            </mui.Typography>
            <mui.Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', fontSize: '0.9rem', maxWidth: '400px' }}>
                {query 
                    ? 'Try adjusting your search criteria or checking for different keywords'
                    : 'You don\'t have any applications assigned to you yet. Please contact your administrator.'
                }
            </mui.Typography>
        </mui.Box>
    );

    return (
        <ThemeProvider theme={theme}>
        <div>
            <SysOwnerSideBar mainContent={customMainContent} />

            <FilterMenu
                id="filter-menu"
                MenuListProps={{
                    'aria-labelledby': 'filter-button',
                }}
            >
                <MenuItem 
                    onClick={handleGroupByCompany} 
                    selected={groupByCompany}
                    sx={{ 
                        fontSize: '0.85rem',
                        fontWeight: groupByCompany ? 600 : 500,
                        color: groupByCompany ? 'primary.main' : 'text.primary',
                        display: 'flex',
                        alignItems: 'center',
                        py: 1.5
                    }}
                >
                    <mui.Box
                        sx={{
                            mr: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            color: groupByCompany ? 'primary.main' : 'text.secondary',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <BusinessIcon fontSize="small" />
                    </mui.Box>
                    Group by Company
                </MenuItem>
                <MenuItem 
                    onClick={handleShowAll} 
                    selected={!groupByCompany}
                    sx={{ 
                        fontSize: '0.85rem',
                        fontWeight: !groupByCompany ? 600 : 500,
                        color: !groupByCompany ? 'primary.main' : 'text.primary',
                        display: 'flex',
                        alignItems: 'center',
                        py: 1.5
                    }}
                >
                    <mui.Box
                        sx={{
                            mr: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            color: !groupByCompany ? 'primary.main' : 'text.secondary',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <mui.SvgIcon fontSize="small">
                            <path d="M4 14h4v-4H4v4zm0 5h4v-4H4v4zM4 9h4V5H4v4zm5 5h12v-4H9v4zm0 5h12v-4H9v4zM9 5v4h12V5H9z" />
                        </mui.SvgIcon>
                    </mui.Box>
                    Show All
                </MenuItem>
            </FilterMenu>
        </div>
        </ThemeProvider>
    );
};

export default ProcessNarrative;
